import type { User } from '@supabase/supabase-js';
import { fetchOwnPortalAccessRow, syncMyPortalAccessAuth } from './accessManagement';
import { resolveAuthEmail } from './authEmail';
import { allowlistRowToCrmUser } from './crmMembers';
import { emailToUsername, normalizeEmail } from './emailUtils';
import {
  fetchOwnPortalDepartmentSlug,
  isElevatedDepartmentSlug,
} from './portalUserProfiles';
import { getActivePortalKind, type DbPortalKind } from './supabase';
import type { AllowedEmail, CrmUser, UserRole } from '../types/crm';

export type AccessResult =
  | { status: 'allowed'; profile: CrmUser }
  | { status: 'not_on_allowlist'; email: string }
  | { status: 'no_email' };

function roleFromWide(kind: DbPortalKind, wide: {
  crm_role: string | null;
  it_role: string | null;
  dem_role: string | null;
  forms_role: string | null;
}): string | null {
  if (kind === 'crm') return wide.crm_role;
  if (kind === 'it') return wide.it_role;
  if (kind === 'dem') return wide.dem_role;
  return wide.forms_role;
}

export async function fetchAllowlistEntry(
  email: string,
  authUserId?: string,
  kind: DbPortalKind = 'crm',
): Promise<AllowedEmail | null> {
  const normalized = normalizeEmail(email);
  const wide = await fetchOwnPortalAccessRow(normalized, authUserId ?? '').catch(() => null);
  if (!wide || wide.removed_at) return null;
  const role = roleFromWide(kind, wide);
  if (!role) return null;
  return {
    id: wide.id,
    email: wide.email,
    display_name: wide.display_name,
    role: role as UserRole,
    username: wide.username,
    auth_id: wide.auth_id,
    department_id: wide.department_id,
    created_at: wide.created_at,
  };
}

function profileFromAllowRow(authUser: User, row: AllowedEmail): CrmUser {
  return {
    ...allowlistRowToCrmUser(row),
    id: authUser.id,
    email: resolveAuthEmail(authUser) ?? normalizeEmail(row.email),
  };
}

export async function ensurePortalUser(
  authUser: User,
  kind: DbPortalKind,
): Promise<AccessResult> {
  const email = resolveAuthEmail(authUser);
  if (!email) return { status: 'no_email' };

  const departmentSlug = await fetchOwnPortalDepartmentSlug(email, authUser.id).catch(() => null);
  if (isElevatedDepartmentSlug(departmentSlug)) {
    const username = emailToUsername(email);
    const displayName =
      (typeof authUser.user_metadata?.full_name === 'string' && authUser.user_metadata.full_name) ||
      (typeof authUser.user_metadata?.name === 'string' && authUser.user_metadata.name) ||
      username.replace(/_/g, ' ');
    await syncMyPortalAccessAuth().catch(() => null);
    return {
      status: 'allowed',
      profile: {
        id: authUser.id,
        username,
        display_name: displayName,
        role: 'it',
        email: normalizeEmail(email),
      },
    };
  }

  await syncMyPortalAccessAuth().catch(() => null);
  const allow = await fetchAllowlistEntry(email, authUser.id, kind);
  if (!allow) {
    return { status: 'not_on_allowlist', email: normalizeEmail(email) };
  }
  return { status: 'allowed', profile: profileFromAllowRow(authUser, allow) };
}

/** Sync allowlist profile for CRM or IT (uses active portal kind from supabase helper). */
export async function ensureCrmUser(authUser: User): Promise<AccessResult> {
  return ensurePortalUser(authUser, getActivePortalKind());
}

/** Sync allowlist profile for DEM Web portal. */
export async function ensureDemUser(authUser: User): Promise<AccessResult> {
  return ensurePortalUser(authUser, 'dem');
}

/** Sync allowlist profile for Application Form portal. */
export async function ensureFormsUser(authUser: User): Promise<AccessResult> {
  return ensurePortalUser(authUser, 'forms');
}
